import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
const questions = [{
  id: 1,
  question: 'Under uncertainty, you usually try to:',
  optionA: 'Create structure and narrow the problem',
  optionB: 'Keep options open and explore the space'
}, {
  id: 2,
  question: "When something isn't working, you're more likely to:",
  optionA: 'Push and try to make it work',
  optionB: 'Step back and reconsider the direction'
}, {
  id: 3,
  question: 'You feel more stressed by:',
  optionA: 'Things being undefined or ambiguous',
  optionB: 'Things being locked in or constrained'
}, {
  id: 4,
  question: 'Your bigger risk in work is usually:',
  optionA: 'Committing too early and feeling stuck',
  optionB: 'Not committing and staying in motion too long'
}, {
  id: 5,
  question: 'When starting something new, you tend to:',
  optionA: 'Go deep into one direction',
  optionB: 'Try multiple directions in parallel'
}, {
  id: 6,
  question: 'In long projects, you more often struggle with:',
  optionA: 'Getting bored once things stabilize',
  optionB: 'Never fully stabilizing anything'
}, {
  id: 7,
  question: 'When you hit resistance, your default move is:',
  optionA: 'Increase control and tighten the system',
  optionB: 'Loosen the system and create space'
}];
// Structural convergence animation - lines assembling into a pattern
const StructuralConvergence = () => {
  return <div className="relative w-full max-w-md mx-auto h-24 sm:h-32" aria-hidden="true" data-id="element-4172">
      <svg viewBox="0 0 400 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-4173">
        {/* Horizontal lines converging */}
        <line x1="0" y1="40" x2="400" y2="40" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '0ms'
      }} data-id="element-4174" />
        <line x1="0" y1="60" x2="400" y2="60" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '300ms'
      }} data-id="element-4175" />
        <line x1="0" y1="80" x2="400" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '600ms'
      }} data-id="element-4176" />

        {/* Vertical connecting lines */}
        <line x1="100" y1="40" x2="100" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '900ms'
      }} data-id="element-4177" />
        <line x1="200" y1="40" x2="200" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '1200ms'
      }} data-id="element-4178" />
        <line x1="300" y1="40" x2="300" y2="80" stroke="#2B2B2B" strokeWidth="1" className="animate-fade-in" style={{
        animationDelay: '1500ms'
      }} data-id="element-4179" />

        {/* Nodes at intersections */}
        <circle cx="100" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '900ms'
      }} data-id="element-4180" />
        <circle cx="200" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1200ms'
      }} data-id="element-4181" />
        <circle cx="300" cy="40" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4182" />
        <circle cx="100" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1200ms'
      }} data-id="element-4183" />
        <circle cx="200" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4184" />
        <circle cx="300" cy="60" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1800ms'
      }} data-id="element-4185" />
        <circle cx="100" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1500ms'
      }} data-id="element-4186" />
        <circle cx="200" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '1800ms'
      }} data-id="element-4187" />
        <circle cx="300" cy="80" r="2" fill="#64748B" className="animate-pulse-slow" style={{
        animationDelay: '2100ms'
      }} data-id="element-4188" />
      </svg>
    </div>;
};
export function WorkMirrorCalibration() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [isCompiling, setIsCompiling] = useState(false);
  const handleAnswer = (answer: 'A' | 'B') => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 150);
    } else {
      // All questions answered, start compiling
      setTimeout(() => {
        setIsCompiling(true);
      }, 150);
    }
  };
  useEffect(() => {
    if (isCompiling) {
      // Show compiling screen for 3 seconds minimum, then navigate
      const timer = setTimeout(() => {
        // Store answers in sessionStorage for the result page
        sessionStorage.setItem('workMirrorAnswers', JSON.stringify(answers));
        navigate('/mirrors/work/result');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isCompiling, answers, navigate]);
  if (isCompiling) {
    return <>
        <Helmet data-id="element-4189">
          <title data-id="element-4190">Compiling your Mirror... | Lazy Monk Labs</title>
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-4 sm:px-6" data-id="element-4191">
          <div className="max-w-3xl w-full text-center space-y-12 sm:space-y-16" data-id="element-4192">
            {/* Title */}
            <div className="space-y-4 sm:space-y-6" data-id="element-4193">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#F5F5F5] leading-tight" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-4194">
                Compiling your Mirror…
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-gray-400 leading-relaxed" data-id="element-4195">
                Building your initial system model.
              </p>
            </div>

            {/* Structural convergence animation */}
            <div className="py-8 sm:py-12" data-id="element-4196">
              <StructuralConvergence data-id="element-4197" />
            </div>

            {/* Optional subtle line */}
            <p className="text-xs sm:text-sm text-gray-600" data-id="element-4198">
              This takes a moment.
            </p>
          </div>
        </div>

        <style data-id="element-4199">{`
          @keyframes fade-in {
            from {
              opacity: 0;
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
            }
            to {
              opacity: 1;
              stroke-dasharray: 1000;
              stroke-dashoffset: 0;
            }
          }

          @keyframes pulse-slow {
            0%, 100% {
              opacity: 0.3;
            }
            50% {
              opacity: 1;
            }
          }

          .animate-fade-in {
            animation: fade-in 0.8s ease-out forwards;
          }

          .animate-pulse-slow {
            animation: pulse-slow 2s ease-in-out infinite;
          }
        `}</style>
      </>;
  }
  const question = questions[currentQuestion];
  const progress = currentQuestion + 1;
  return <>
      <Helmet data-id="element-4200">
        <title data-id="element-4201">Calibration — Work Mirror | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4202">
        {/* Progress indicator */}
        <div className="fixed top-0 left-0 right-0 z-10 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4203">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4" data-id="element-4204">
            <p className="text-xs sm:text-sm font-mono text-gray-600" data-id="element-4205">
              {progress} of {questions.length}
            </p>
          </div>
        </div>

        {/* Question content */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pt-16 sm:pt-20 pb-20 sm:pb-24" data-id="element-4206">
          <div className="max-w-3xl w-full space-y-8 sm:space-y-12" data-id="element-4207">
            {/* First question hint */}
            {currentQuestion === 0 && <p className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-4208">
                There are no right answers. Pick what you actually do.
              </p>}

            {/* Question */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-[#F5F5F5] text-center leading-tight px-2" style={{
            fontFamily: 'Georgia, serif'
          }} data-id="element-4209">
              {question.question}
            </h1>

            {/* Options */}
            <div className="space-y-3 sm:space-y-4 pt-6 sm:pt-8" data-id="element-4210">
              <button onClick={() => handleAnswer('A')} className="w-full p-6 sm:p-8 border border-[#2B2B2B] text-left text-base sm:text-lg text-[#F5F5F5] hover:border-[#F5F5F5] hover:bg-[#F5F5F5]/5 transition-all active:scale-[0.99]" data-id="element-4211">
                <span className="text-gray-600 mr-3 sm:mr-4" data-id="element-4212">A)</span>
                {question.optionA}
              </button>

              <button onClick={() => handleAnswer('B')} className="w-full p-6 sm:p-8 border border-[#2B2B2B] text-left text-base sm:text-lg text-[#F5F5F5] hover:border-[#F5F5F5] hover:bg-[#F5F5F5]/5 transition-all active:scale-[0.99]" data-id="element-4213">
                <span className="text-gray-600 mr-3 sm:mr-4" data-id="element-4214">B)</span>
                {question.optionB}
              </button>
            </div>
          </div>
        </div>

        {/* Subtle back button (non-prominent) */}
        {currentQuestion > 0 && <div className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6" data-id="element-4215">
            <button onClick={() => {
          setCurrentQuestion(currentQuestion - 1);
          setAnswers(answers.slice(0, -1));
        }} className="text-xs sm:text-sm text-gray-600 hover:text-gray-400 transition-colors p-2" data-id="element-4216">
              ← Back
            </button>
          </div>}
      </div>
    </>;
}